import { ResiPermissions } from './resi-permissions';

export function buildResiPermissionSet({
    isResident,
    isRental,
    isPrimary,
    isMinor,
    canInvite,
    isTenant,
    isTenantLongTerm,
    isGuest,
    hasMyVehiclesModule,
    hasShortTermRentalModule,
    isOwnerManaged,
    isLimited,
    isGated,
    isSuspended
}) {
    const permissions = new Set();

    /** Default permission, anyone with an access key can and should be able to see them */
    permissions.add(ResiPermissions.MANAGE_ACCESS_KEY);

    // if (isSuspended) {
    //     permissions.add(ResiPermissions.MANAGE_ACCESS_KEY);
    //     return permissions;
    // }

    /**
     * Suspended users should not control property-level access.
     */
    if (isSuspended) {
        // permissions.add(ResiPermissions.MANAGE_ACCESS_KEY);
        return permissions;
    }

    /**
     * Guest users should only be allowed to manage vehicles like ios does.
     */
    if (isGuest) {
        permissions.add(ResiPermissions.VIEW_VEHICLES);
        permissions.add(ResiPermissions.MANAGE_VEHICLES);
        return permissions;
    }

    /**
     * Minors can view limited resident data only when the property
     * is not rented out from under them.
     */
    if (isMinor) {
        permissions.add(ResiPermissions.VIEW_GUESTS);
        permissions.add(ResiPermissions.VIEW_OCCUPANTS);
        return permissions;
    }

    /**
     * Rental-owner restriction:
     *
     * If this user is a resident owner on a rented property,
     * they should not see or manage the tenant's guest/occupant/vehicle data.
     *
     */
    const isResidentOwnerOnRentalProperty = isResident && isRental && !isTenantLongTerm;

    if (isResidentOwnerOnRentalProperty) {
        permissions.add(ResiPermissions.VIEW_GUESTS);
        permissions.add(ResiPermissions.INVITE_GUESTS);
        /**
         * The resident/owner can always create their own OTC (flat 30-day validity,
         * unrelated to the tenant's rental window) even while the unit is rented out.
         */
        permissions.add(ResiPermissions.MANAGE_OTC);

        /**
         * Owner-managed rental properties are run by the owner, so any resident of one
         * (primary or not, never a minor, who returned above) manages its reservations.
         */
        if (hasShortTermRentalModule && isOwnerManaged) {
            permissions.add(ResiPermissions.MANAGE_RENTALS);
        }

        /**
         * The primary owner still manages vehicles on their own property,
         * even while it is rented out.
         */
        if (hasMyVehiclesModule && isPrimary) {
            permissions.add(ResiPermissions.VIEW_VEHICLES);
            permissions.add(ResiPermissions.MANAGE_VEHICLES);
        }

        return permissions;
    }

    /**
     * Baseline resident/tenant visibility.
     */
    permissions.add(ResiPermissions.VIEW_GUESTS);
    permissions.add(ResiPermissions.VIEW_OCCUPANTS);

    if (hasMyVehiclesModule) {
        permissions.add(ResiPermissions.VIEW_VEHICLES);
        permissions.add(ResiPermissions.MANAGE_VEHICLES);
    }

    /**
     * Any tenant (short- or long-term) can invite guests, just like a resident.
     * Only OTC generation is restricted to long-term (non-short-term) tenants.
     */
    const isManagedResidentType = isResident || isTenant;

    if (isManagedResidentType) {
        if (canInvite) {
            permissions.add(ResiPermissions.INVITE_GUESTS);
            permissions.add(ResiPermissions.MANAGE_ACCESS_KEY);
            permissions.add(ResiPermissions.MANAGE_VISITORS);

            if (isResident || isTenantLongTerm) {
                permissions.add(ResiPermissions.MANAGE_OTC);
            }
        }

        if (isPrimary && !isTenant) {
            permissions.add(ResiPermissions.INVITE_OCCUPANTS);
        }

        if (!hasMyVehiclesModule) {
            permissions.delete(ResiPermissions.VIEW_VEHICLES);
            permissions.delete(ResiPermissions.MANAGE_VEHICLES);
        }

        if (isGated) {
            permissions.delete(ResiPermissions.INVITE_GUESTS);
            permissions.delete(ResiPermissions.MANAGE_OTC);
            permissions.delete(ResiPermissions.INVITE_OCCUPANTS);
            permissions.delete(ResiPermissions.MANAGE_VEHICLES);
        }

        if (isLimited) {
            permissions.delete(ResiPermissions.MANAGE_OTC);
            permissions.delete(ResiPermissions.INVITE_OCCUPANTS);
            permissions.delete(ResiPermissions.MANAGE_VEHICLES);
        }
    }

    return permissions;
}
